import { addDays, subDays } from "date-fns";
import React from "react";
import BiofixDateInput from "../../components/models/biofixDateInput";
import ManagementGuide from "../../components/models/managementGuide";
import ModelLayout from "../../components/models/modelLayout";
import PhenologicalImage from "../../components/models/phenologicalImage";
import GlobalStateContext from "../../context/globalStateContext";
import { useAuth0 } from "../../context/react-auth0-spa";
import { modelInSeason, sdateEdate } from "../../utils/helpers";
import useFetchStationData from "../../utils/hooks/useFetchStationData";
import useStations from "../../utils/hooks/useStations";
import modelData from "./oriental-fruit-moth.json";
import Acknowledgments from "./_acknowledgements";
import Dropdown from "./_dropdown";
import modelLogic from "./_modelLogic";
import MoreInfo from "./_moreInfo";
import References from "./_references";
import ResultsGraph from "./_resultsGraph";
import ResultsTable from "./_resultsTable";

const phenologicalStages = Object.values(
  modelData.elements.managementGuide.phenologicalStages
).map((d) => {
  return {
    value: d.value,
    label: d.value,
    beforeBiofix1: d.beforeBiofix1,
  };
});

const pestStages = (biofix1, biofix2, biofix3) => {
  let res = [];
  if (biofix1 && !biofix2 && !biofix3) {
    for (const [key, val] of Object.entries(
      modelData.elements.managementGuide.afterBiofix1
    )) {
      res.push({
        value: val.pestStage,
        label: val.pestStage,
        key,
      });
    }
  }
  if (biofix1 && biofix2 && !biofix3) {
    for (const [key, val] of Object.entries(
      modelData.elements.managementGuide.afterBiofix2
    )) {
      res.push({
        value: val.pestStage,
        label: val.pestStage,
        key,
      });
    }
  }

  if (biofix1 && biofix2 && biofix3) {
    for (const [key, val] of Object.entries(
      modelData.elements.managementGuide.afterBiofix3
    )) {
      res.push({
        value: val.pestStage,
        label: val.pestStage,
        key,
      });
    }
  }
  return res;
};

const visibleElements = Object.entries(modelData.elements)
  .filter(([_, value]) => value.priority)
  .filter(([key, _]) => !key.includes("user"))
  .map((arr) => ({ ...arr[1], toggleName: arr[0] }))
  .sort((a, b) =>
    a.priority > b.priority ? 1 : b.priority > a.priority ? -1 : 0
  );

export default function OrientalFruitMoth() {
  const h1Text = modelData.title;
  const { title, id, elements, seasonStart, seasonEnd } = modelData;
  const { managementGuide } = modelData.elements;
  const { isAuthenticated, user } = useAuth0();
  const { stationList, station, setStation, favoriteStations, geoJSON } =
    useStations();
  const { dateOfInterest } = React.useContext(GlobalStateContext);
  const isModelInSeason = modelInSeason(dateOfInterest, modelData);

  const { sdate, edate } = sdateEdate(dateOfInterest, seasonEnd);
  const { isLoading, data } = useFetchStationData(station, sdate, edate);

  const [userBiofix1, setUserBiofix1] = React.useState(null);
  const [userBiofix2, setUserBiofix2] = React.useState(null);
  const [userBiofix3, setUserBiofix3] = React.useState(null);
  const [userPhenologicalStage, setUserPhenologicalStage] =
    React.useState(null);
  const [userPestStage, setUserPestStage] = React.useState(null);

  let mData = null;
  if (data && isModelInSeason) {
    mData = modelLogic(
      modelData,
      data,
      dateOfInterest,
      isModelInSeason,
      userBiofix1,
      userBiofix2,
      userBiofix3,
      userPhenologicalStage,
      userPestStage
    );
  }

  const [isMoreInfo, setIsMoreInfo] = React.useState(false);
  const [showManagementGuide, setShowManagementGuide] = React.useState(true);
  const [showResultsTable, setShowResultsTable] = React.useState(true);
  const [showResultsGraph, setShowResultsGraph] = React.useState(false);

  function resetUserData() {
    setUserBiofix1(null);
    setUserBiofix2(null);
    setUserBiofix3(null);
    setUserPhenologicalStage(null);
    setUserPestStage(null);
  }

  React.useEffect(() => {
    resetUserData();
  }, [dateOfInterest]);

  React.useEffect(() => {
    if (!user) return;

    if (Object.keys(user.activeTools)) {
      const model = user.activeTools.find((model) => model.id === id);
      if (model) {
        if (
          Object.keys(model.biofix1).includes(`${dateOfInterest.year}`) &&
          Object.keys(model.biofix1[dateOfInterest.year]).includes(station.id)
        ) {
          setUserBiofix1(model.biofix1[dateOfInterest.year][station.id]);
        } else {
          setUserBiofix1(null);
        }
        if (
          Object.keys(model.biofix2).includes(`${dateOfInterest.year}`) &&
          Object.keys(model.biofix2[dateOfInterest.year]).includes(station.id)
        ) {
          setUserBiofix2(model.biofix2[dateOfInterest.year][station.id]);
        } else {
          setUserBiofix2(null);
        }
        if (
          Object.keys(model.biofix3).includes(`${dateOfInterest.year}`) &&
          Object.keys(model.biofix3[dateOfInterest.year]).includes(station.id)
        ) {
          setUserBiofix3(model.biofix3[dateOfInterest.year][station.id]);
        } else {
          setUserBiofix3(null);
        }
      }
    }
  }, [station, id, user, dateOfInterest]);

  const isVisible = station && mData && !isLoading;

  return (
    <ModelLayout
      isAuthenticated={isAuthenticated}
      stationList={user ? favoriteStations : stationList}
      station={station}
      setStation={setStation}
      allStations={stationList}
      geoJSON={geoJSON}
      data={data}
      isModelInSeason={isModelInSeason}
      isLoading={isLoading}
      modelData={modelData}
      visibleElements={visibleElements}
      moreInfo={<MoreInfo station={station} moreInfo={modelData.moreInfo} />}
      setIsMoreInfo={setIsMoreInfo}
      references={References}
      acknowledgments={Acknowledgments}
      isMoreInfo={isMoreInfo}
      showManagementGuide={showManagementGuide}
      setShowManagementGuide={setShowManagementGuide}
      showResultsTable={showResultsTable}
      setShowResultsTable={setShowResultsTable}
      showResultsChart={showResultsGraph}
      setShowResultsChart={setShowResultsGraph}
      tutorialLink={modelData.tutorialLink}
      h1Text={h1Text}
    >
      <div className={`mb-16 sm:mb-18 md:mb-20`}>
        {isModelInSeason && isVisible && (
          <>
            <h2 className="mb-3 font-semibold text-gray-600 sm:mb-5 md:mb-6 md:text-2xl">
              {mData.biofix1 === null &&
                mData.biofix2 === null &&
                `First Trap Catch`}

              {mData.biofix1 &&
                mData.biofix2 === null &&
                elements.userInputs.firstInput.label}

              {mData.biofix1 &&
                mData.biofix2 &&
                mData.biofix3 === null &&
                `First Trap Catch of the 1st & 2nd Generations`}

              {mData.biofix1 &&
                mData.biofix2 &&
                mData.biofix3 &&
                `First Trap Catch of the 1st, 2nd & 3rd Generations`}
            </h2>
            <div className="px-6 py-6 bg-white rounded-lg shadow-lg">
              {mData.selectedDate.displayBiofix1Input && (
                <div className="flex items-center justify-center py-6">
                  <BiofixDateInput
                    modelId={id}
                    id={"biofix1"}
                    dbKey="biofix1"
                    minDate={new Date(`${dateOfInterest.year}-${seasonStart}`)}
                    maxDate={
                      mData.biofix2
                        ? subDays(mData.biofix2.date, 1)
                        : dateOfInterest.date
                    }
                    biofix={mData.biofix1}
                    userBiofix={userBiofix1}
                    setUserBiofix={setUserBiofix1}
                    label={elements.userInputs.firstInput.label}
                    modelName={title}
                    tooltip={`Reset biofix to default value`}
                    datesWithNoData={mData.daysWithMoreThanFiveMissingHours}
                    station={station}
                  ></BiofixDateInput>
                </div>
              )}

              {mData.biofix2 && (
                <div className="flex items-center justify-center py-6">
                  <BiofixDateInput
                    modelId={id}
                    id={"biofix2"}
                    dbKey="biofix2"
                    minDate={mData.biofix1 && addDays(mData.biofix1.date, 1)}
                    maxDate={
                      mData.biofix3
                        ? subDays(mData.biofix3.date, 1)
                        : dateOfInterest.date
                    }
                    biofix={mData.biofix2}
                    userBiofix={userBiofix2}
                    setUserBiofix={setUserBiofix2}
                    label={elements.userInputs.secondInput.label}
                    modelName={title}
                    tooltip={`Reset biofix to default value`}
                    datesWithNoData={mData.daysWithMoreThanFiveMissingHours}
                    station={station}
                  ></BiofixDateInput>
                </div>
              )}

              {mData.biofix3 && (
                <div className="flex items-center justify-center py-6">
                  <BiofixDateInput
                    modelId={id}
                    id={"biofix3"}
                    dbKey="biofix3"
                    minDate={mData.biofix2 && addDays(mData.biofix2.date, 1)}
                    maxDate={dateOfInterest.date}
                    biofix={mData.biofix3}
                    userBiofix={userBiofix3}
                    setUserBiofix={setUserBiofix3}
                    label={elements.userInputs.thirdInput.label}
                    modelName={title}
                    tooltip={`Reset biofix to default value`}
                    datesWithNoData={mData.daysWithMoreThanFiveMissingHours}
                    station={station}
                  ></BiofixDateInput>
                </div>
              )}

              <div className="flex items-center justify-center">
                <div className="flex flex-col items-center py-2">
                  {mData.selectedDate.displayBiofix1Input && (
                    <p className="max-w-3xl p-2 mx-auto mb-6 text-gray-700 rounded-md bg-gray-50">
                      {mData.userInputMsg}
                    </p>
                  )}

                  <p className="max-w-3xl mx-auto">
                    {mData.accumulationDDMsg.text}{" "}
                    <b>{mData.accumulationDDMsg.gdd}</b>
                  </p>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Results Table */}
      {isModelInSeason && isVisible && showResultsTable && (
        <div className="mb-16 sm:mb-18 md:mb-20">
          <ResultsTable
            station={station}
            modelData={modelData}
            tableData={mData.tableData}
            csvData={mData.csvData}
            biofix1={mData.biofix1}
            biofix2={mData.biofix2}
            biofix3={mData.biofix3}
          ></ResultsTable>
        </div>
      )}

      {/* Results Graph */}
      {isModelInSeason && isVisible && showResultsGraph && (
        <div
          className="mb-16 sm:mb-18 md:mb-20"
          aria-label="A line Graph of Results Data"
        >
          <ResultsGraph
            modelData={modelData}
            data={mData.data}
            biofix1={mData.biofix1}
            biofix2={mData.biofix2}
            biofix3={mData.biofix3}
          ></ResultsGraph>
        </div>
      )}

      {/* Management Guide */}
      {isVisible && showManagementGuide && (
        <div>
          <h2 className="mb-3 font-semibold text-gray-600 sm:mb-5 md:mb-6 md:text-2xl">
            {managementGuide.title}
          </h2>

          {isModelInSeason && (
            <div>
              {/* Phenological Stage */}
              {!mData.biofix1 && (
                <div className="px-6 py-6 mb-12 bg-white rounded-lg shadow-lg">
                  <div className="flex flex-col justify-center w-full max-w-xl mx-auto text-gray-700">
                    <div className="items-center justify-between sm:flex">
                      <div className="flex items-center mb-6">
                        <span className="mr-2 font-bold">
                          Phenological Stage:
                        </span>
                        <Dropdown
                          width="w-64"
                          id="phenological-stages"
                          placeholder="Select Phenological Stage"
                          ariaLabel="phenological stage"
                          list={phenologicalStages}
                          item={mData.phenologicalStage}
                          setUserItem={setUserPhenologicalStage}
                        ></Dropdown>
                      </div>

                      <div className="flex flex-col items-center text-center">
                        Click to enlarge
                        <PhenologicalImage
                          phenologicalStage={mData.phenologicalStage}
                        ></PhenologicalImage>
                      </div>
                    </div>
                  </div>

                  {!userPhenologicalStage && (
                    <div className="flex items-center justify-center w-full mt-6 text-gray-700">
                      The phenological stage above is estimated. Select the
                      actual stage and the model will recalculate
                      recommendations.
                    </div>
                  )}
                </div>
              )}

              {/* Pest Stage */}
              <div className="flex items-center justify-center w-full mb-12 text-gray-700">
                <span className="mr-2 text-lg font-bold">Pest Stage:</span>
                {mData.biofix1 ? (
                  <Dropdown
                    width="w-96"
                    id="pest-stages"
                    placeholder="Select Pest Stage"
                    ariaLabel="pest stage"
                    list={pestStages(
                      mData.biofix1,
                      mData.biofix2,
                      mData.biofix3
                    )}
                    item={mData.pestStage}
                    setUserItem={setUserPestStage}
                  ></Dropdown>
                ) : (
                  <span className="text-lg">
                    {mData.managementGuide.pestStage}
                  </span>
                )}
              </div>

              {mData.biofix1 && (
                <div>
                  {userPestStage === null ? (
                    <div className="flex items-center justify-center w-full mb-4 text-gray-700 sm:mb-6 md:mb-7">
                      The pest stage above is estimated. Select the actual stage
                      and the model will recalculate recommendations.
                    </div>
                  ) : (
                    <div className="flex items-center justify-center w-full mb-4 text-gray-700 sm:mb-6 md:mb-7">
                      Change the pest stage above and the model will recalculate
                      recommendations.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {!isLoading && mData && showManagementGuide && (
        <div className={`mb-16 sm:mb-18 md:mb-20`}>
          {!isModelInSeason && (
            <h2 className="mb-3 font-semibold text-gray-600 sm:mb-5 md:mb-6 md:text-2xl">
              {managementGuide.title}
            </h2>
          )}
          <ManagementGuide
            managementGuideMsg={
              isModelInSeason
                ? mData.managementGuide
                : managementGuide.outOfSeasonMsg
            }
            setIsMoreInfo={setIsMoreInfo}
          ></ManagementGuide>
        </div>
      )}
    </ModelLayout>
  );
}
